import React, { useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';

const AdminChat = ({ adminName, onLogout }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedWorkstation, setSelectedWorkstation] = useState(null);
  const [notifications, setNotifications] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [onlineClients, setOnlineClients] = useState({});
  const [hoveredKey, setHoveredKey] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regMessage, setRegMessage] = useState('');
  
  // New state for download modal and its settings
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadCompany, setDownloadCompany] = useState('');
  const [downloadOS, setDownloadOS] = useState('win'); // 'win' or 'mac'
  const [isDownloading, setIsDownloading] = useState(false);

  const chatEndRef = useRef(null);
  const socketRef = useRef(null);
  // const SERVER_URL = 'http://localhost:3001';
  const SERVER_URL = 'https://chat-api.b2bdev.net';

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }
  }, []);

  // Function to send a test notification
  const sendTestNotification = () => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        const notification = new Notification('Test Notification', {
          body: 'This is a test notification.',
          icon: '/path/to/icon.png' // replace with your icon path
        });
        notification.onclick = () => {
          window.focus();
        };
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            const notification = new Notification('Test Notification', {
              body: 'This is a test notification.',
              icon: '/path/to/icon.png'
            });
            notification.onclick = () => {
              window.focus();
            };
          }
        });
      }
    }
  };

  // Initialize socket once
  useEffect(() => {
    socketRef.current = io(SERVER_URL, {
      auth: {
        role: "admin",
        adminName: adminName,
        token: localStorage.getItem('token')
      }
    });
    return () => {
      socketRef.current.disconnect();
    };
  }, [adminName, SERVER_URL]);

  // Fetch online clients on initial load.
  useEffect(() => {
    axios.get(`${SERVER_URL}/online-clients`)
      .then(res => {
        const clients = res.data;
        const onlineMapping = {};
        clients.forEach(client => {
          const key = `${client.companyId}_${client.workstationId}`;
          onlineMapping[key] = true;
        });
        setOnlineClients(onlineMapping);
      })
      .catch(err => console.error("Error fetching online clients:", err));
  }, [SERVER_URL]);

  useEffect(() => {
    // Fetch initial chat messages.
    axios.get(`${SERVER_URL}/messages`)
      .then(res => setMessages(res.data))
      .catch(err => console.error('Error fetching messages:', err));

    socketRef.current.on('chat message', (msg) => {
      setMessages(prevMessages => [...prevMessages, msg]);
      if (!(msg.companyId === selectedCompany && msg.workstationId === selectedWorkstation)) {
        const key = `${msg.companyId}_${msg.workstationId}`;
        setNotifications(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
        // Trigger a browser notification if the document is hidden
        if (document.visibilityState === 'hidden' && 'Notification' in window && Notification.permission === 'granted') {
          const notification = new Notification(`New message from ${msg.companyId}`, {
            body: msg.message,
            icon: '/path/to/icon.png' // replace with your icon path if needed
          });
          notification.onclick = () => window.focus();
        }
      }
    });

    socketRef.current.on('clients update', (clients) => {
      const onlineMapping = {};
      clients.forEach(client => {
        const key = `${client.companyId}_${client.workstationId}`;
        onlineMapping[key] = true;
      });
      setOnlineClients(onlineMapping);
    });

    return () => {
      socketRef.current.off('chat message');
      socketRef.current.off('clients update');
    };
  }, [selectedCompany, selectedWorkstation, SERVER_URL]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, selectedCompany, selectedWorkstation]);

  const groupMessages = () => {
    const groups = {};
    messages.forEach(msg => {
      if (!groups[msg.companyId]) groups[msg.companyId] = {};
      if (!groups[msg.companyId][msg.workstationId])
        groups[msg.companyId][msg.workstationId] = [];
      groups[msg.companyId][msg.workstationId].push(msg);
    });
    return groups;
  };

  const groups = groupMessages();

  const recentChats = () => {
    const conversationMap = {};
    messages.forEach(msg => {
      const key = `${msg.companyId}_${msg.workstationId}`;
      if (!conversationMap[key] || new Date(msg.createdAt) > new Date(conversationMap[key].createdAt)) {
        conversationMap[key] = msg;
      }
    });
    return Object.values(conversationMap).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const filteredRecentChats = recentChats().filter(msg => {
    const query = searchQuery.toLowerCase();
    return (
      msg.companyId.toLowerCase().includes(query) ||
      msg.workstationId.toLowerCase().includes(query)
    );
  });

  const limitedRecentChats = filteredRecentChats.slice(0, 5);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!selectedCompany || !selectedWorkstation) {
      alert("Please select a company and workstation.");
      return;
    }
    if (input.trim()) {
      const newMsg = {
        message: input,
        sender: adminName,
        companyId: selectedCompany,
        workstationId: selectedWorkstation
      };
      socketRef.current.emit('chat message', newMsg);
      setInput('');
      const key = `${selectedCompany}_${selectedWorkstation}`;
      setNotifications(prev => ({ ...prev, [key]: 0 }));
    }
  };

  const handleSelectConversation = (company, workstation) => {
    setSelectedCompany(company);
    setSelectedWorkstation(workstation);
    const key = `${company}_${workstation}`;
    setNotifications(prev => ({ ...prev, [key]: 0 }));
  };

  const handleDeleteConversation = (e, company, workstation) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete all messages for ${company} - ${workstation}?`)) {
      axios.delete(`${SERVER_URL}/messages/${company}/${workstation}`)
        .then(res => {
          setMessages(prevMessages => prevMessages.filter(
            msg => !(msg.companyId === company && msg.workstationId === workstation)
          ));
          const key = `${company}_${workstation}`;
          setNotifications(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
          });
          if (selectedCompany === company && selectedWorkstation === workstation) {
            setSelectedCompany(null);
            setSelectedWorkstation(null);
          }
        })
        .catch(err => {
          console.error("Error deleting conversation:", err);
          alert("There was an error deleting the conversation.");
        });
    }
  };

  // Handle registration submission.
  const handleRegister = async (e) => {
    e.preventDefault();
    if (regPassword !== regConfirmPassword) {
      setRegMessage("Passwords do not match.");
      return;
    }
    try {
      const res = await axios.post(`${SERVER_URL}/register`, {
        username: regUsername,
        password: regPassword
      });
      if (res.data.success) {
        setRegMessage("Account created successfully.");
        setTimeout(() => {
          setShowRegister(false);
          setRegUsername('');
          setRegPassword('');
          setRegConfirmPassword('');
          setRegMessage('');
        }, 1500);
      } else {
        setRegMessage(res.data.message);
      }
    } catch (err) {
      console.error(err);
      setRegMessage("Error creating account.");
    }
  };

  // Logout handler.
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminUsername');
    if (onLogout) onLogout();
    else window.location.reload();
  };

  // Handle download installer.
  const handleDownloadInstaller = async () => {
    if (!downloadCompany) {
      alert("Please enter a company name.");
      return;
    }
    setIsDownloading(true);
    try {
      const response = await axios.get(`${SERVER_URL}/custom-build`, {
        params: { company: downloadCompany, os: downloadOS },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadOS === 'win'
        ? `B2BChat-${downloadCompany}.exe`
        : `B2BChat-${downloadCompany}.dmg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowDownloadModal(false);
    } catch (error) {
      console.error("Error during custom build:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif', backgroundColor: '#f0f2f5' }}>
      {/* Hidden Admin Menu Icon */}
      <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1000 }}>
        <div 
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#4A90E2',
            color: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            fontSize: '18px'
          }}
        >
          ☰
        </div>
        {menuOpen && (
          <div style={{
            position: 'absolute',
            top: '50px',
            right: '0',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            width: '200px',
            zIndex: 1000,
            padding: '10px'
          }}>
            <button
              onClick={() => {
                sendTestNotification();
                setMenuOpen(false);
              }}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '6px',
                background: '#4A90E2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Test Notification
            </button>
            <button 
              onClick={() => {
                setShowRegister(true);
                setMenuOpen(false);
              }}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '6px',
                background: '#4A90E2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Create Account
            </button>
            <button 
              onClick={() => {
                setDownloadCompany(selectedCompany || '');
                setShowDownloadModal(true);
                setMenuOpen(false);
              }}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '6px',
                background: '#4A90E2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Download Installer
            </button>
            <button 
              onClick={() => {
                setMenuOpen(false);
                handleLogout();
              }}
              style={{
                width: '100%',
                padding: '8px',
                background: '#e74c3c',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Left Sidebar */}
      <div style={{
          width: '300px',
          backgroundColor: '#fff',
          borderRight: '1px solid #ddd',
          padding: '20px',
          overflowY: 'auto',
          boxShadow: '2px 0 5px rgba(0,0,0,0.05)'
        }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by company or workstation"
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '15px',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />

        <h2 style={{ marginTop: 0 }}>Recent Chats</h2>
        {limitedRecentChats.length === 0 && <p>No chats found.</p>}
        {limitedRecentChats.map(msg => {
          const key = `${msg.companyId}_${msg.workstationId}`;
          const isSelected = msg.companyId === selectedCompany && msg.workstationId === selectedWorkstation;
          return (
            <button 
              key={key}
              onClick={() => handleSelectConversation(msg.companyId, msg.workstationId)}
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
              style={{
                background: isSelected ? '#4A90E2' : '#f8f9fa',
                color: isSelected ? '#fff' : '#333',
                width: '100%',
                padding: '12px',
                marginBottom: '10px',
                border: 'none',
                textAlign: 'left',
                borderRadius: '5px',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                position: 'relative'
              }}
            >
              <div style={{ fontWeight: 'bold' }}>
                {msg.companyId} - {msg.workstationId}
                {onlineClients[key] && (
                  <span style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#27ae60',
                    marginLeft: '8px'
                  }}></span>
                )}
              </div>
              <div style={{ fontSize: '0.8em', color: isSelected ? '#e0e0e0' : '#777' }}>
                {new Date(msg.createdAt).toLocaleString()}
              </div>
              {notifications[key] > 0 && (
                <span style={{
                  background: '#e74c3c',
                  color: '#fff',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  float: 'right',
                  fontSize: '0.8em'
                }}>
                  {notifications[key]}
                </span>
              )}
              {hoveredKey === key && (
                <span
                  onClick={(e) => handleDeleteConversation(e, msg.companyId, msg.workstationId)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: 'red',
                    fontWeight: 'bold',
                    opacity: 0.8,
                    transition: 'opacity 0.3s'
                  }}
                >
                  ×
                </span>
              )}
            </button>
          );
        })}

        <h2 style={{ marginTop: '30px' }}>Companies</h2>
        {Object.keys(groups).length === 0 && <p>No messages yet.</p>}
        {Object.keys(groups).map(company => {
          const query = searchQuery.toLowerCase();
          const companyMatches = company.toLowerCase().includes(query);
          const workstations = Object.keys(groups[company]);
          const filteredWorkstations = companyMatches ? workstations : workstations.filter(ws => ws.toLowerCase().includes(query));
          if (!companyMatches && filteredWorkstations.length === 0) return null;
          return (
            <div key={company} style={{ marginBottom: '15px' }}>
              <button 
                onClick={() => {
                  if (selectedCompany === company) {
                    setSelectedCompany(null);
                    setSelectedWorkstation(null);
                  } else {
                    setSelectedCompany(company);
                    setSelectedWorkstation(null);
                  }
                }}
                style={{
                  background: company === selectedCompany ? '#4A90E2' : '#f8f9fa',
                  color: company === selectedCompany ? '#fff' : '#333',
                  width: '100%',
                  padding: '12px',
                  border: 'none',
                  textAlign: 'left',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  position: 'relative'
                }}
              >
                {company}
                {workstations.some(ws => onlineClients[`${company}_${ws}`]) && (
                  <span style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#27ae60',
                    marginLeft: '8px'
                  }}></span>
                )}
              </button>
              {selectedCompany === company && (
                <div style={{ paddingLeft: '10px', marginTop: '8px' }}>
                  {filteredWorkstations.map(ws => {
                    const key = `${company}_${ws}`;
                    const isWsSelected = ws === selectedWorkstation;
                    return (
                      <button 
                        key={ws}
                        onClick={() => handleSelectConversation(company, ws)}
                        onMouseEnter={() => setHoveredKey(key)}
                        onMouseLeave={() => setHoveredKey(null)}
                        style={{
                          background: isWsSelected ? '#4A90E2' : '#e9ecef',
                          color: isWsSelected ? '#fff' : '#333',
                          width: '100%',
                          padding: '8px',
                          marginTop: '6px',
                          border: 'none',
                          textAlign: 'left',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          position: 'relative'
                        }}
                      >
                        {ws}
                        {onlineClients[key] && (
                          <span style={{
                            display: 'inline-block',
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: '#27ae60',
                            marginLeft: '8px'
                          }}></span>
                        )}
                        {notifications[key] > 0 && (
                          <span style={{
                            background: '#e74c3c',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            float: 'right',
                            fontSize: '0.8em'
                          }}>
                            {notifications[key]}
                          </span>
                        )}
                        {hoveredKey === key && (
                          <span
                            onClick={(e) => handleDeleteConversation(e, company, ws)}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'pointer',
                              color: 'red',
                              fontWeight: 'bold',
                              opacity: 0.8,
                              transition: 'opacity 0.3s'
                            }}
                          >
                            ×
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Chat Area */}
      <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          backgroundColor: '#f0f2f5'
        }}>
        <div style={{
            flex: 1,
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '15px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '15px'
          }}>
          {selectedCompany && selectedWorkstation ? (
            <>
              <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                Chat for {selectedCompany} - {selectedWorkstation}
              </h3>
              {(groups[selectedCompany] && groups[selectedCompany][selectedWorkstation]) ? (
                groups[selectedCompany][selectedWorkstation]
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                .map(msg => (
                  <div 
                    key={msg._id} 
                    style={{
                      marginBottom: '12px', 
                      padding: '10px', 
                      background: '#f9f9f9', 
                      borderRadius: '8px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                  >
                    <strong>{msg.sender}</strong>: {msg.message}
                    <div style={{ fontSize: '0.8em', color: '#777', marginTop: '4px' }}>
                      {new Date(msg.createdAt).toLocaleString()}
                    </div>
                  </div>
                ))
              ) : (
                <p>No messages sent.</p>
              )}
              <div ref={chatEndRef} />
            </>
          ) : (
            <p>Please select a company and workstation from the sidebar.</p>
          )}
        </div>
        
        <form onSubmit={handleSendMessage} style={{ display: 'flex' }}>
          <input 
            type="text" 
            value={input} 
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
          <button 
            type="submit"
            style={{
              padding: '12px 20px',
              marginLeft: '10px',
              background: '#4A90E2',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Send
          </button>
        </form>
      </div>

      {/* Download Installer Modal */}
      {showDownloadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            width: '300px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            <h3>Download Installer</h3>
            <div style={{ marginBottom: '10px' }}>
              <label>Company Name:</label>
              <input
                type="text"
                value={downloadCompany}
                onChange={(e) => setDownloadCompany(e.target.value)}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label>Select OS:</label>
              <div>
                <label style={{ marginRight: '10px' }}>
                  <input
                    type="radio"
                    name="os"
                    value="win"
                    checked={downloadOS === 'win'}
                    onChange={() => setDownloadOS('win')}
                  /> Windows
                </label>
                <label>
                  <input
                    type="radio"
                    name="os"
                    value="mac"
                    checked={downloadOS === 'mac'}
                    onChange={() => setDownloadOS('mac')}
                  /> macOS
                </label>
              </div>
            </div>
            {isDownloading ? (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <img src="/loading.gif" alt="Loading..." style={{ width: '100px', height: '100px', marginBottom: '3px' }} />
                <p>Please do not leave this page while we build your installer.</p>
                <p>It can take up to 30 seconds.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowDownloadModal(false)}
                  style={{
                    marginRight: '10px',
                    padding: '8px 12px',
                    background: '#ccc',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >Cancel</button>
                <button
                  onClick={handleDownloadInstaller}
                  style={{
                    padding: '8px 12px',
                    background: '#4A90E2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >Download</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Registration Modal */}
      {showRegister && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            width: '300px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            <h3>Create Account</h3>
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: '10px' }}>
                <label>Username:</label>
                <input
                  type="text"
                  value={regUsername}
                  onChange={(e) => setRegUsername(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>Password:</label>
                <input
                  type="password"
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>Confirm Password:</label>
                <input
                  type="password"
                  value={regConfirmPassword}
                  onChange={(e) => setRegConfirmPassword(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                />
              </div>
              {regMessage && <p style={{ color: 'red' }}>{regMessage}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowRegister(false)}
                  style={{
                    marginRight: '10px',
                    padding: '8px 12px',
                    background: '#ccc',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 12px',
                    background: '#4A90E2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Create Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChat;
